import { brandingApi, toBrandConfig } from "@/lib/client/branding-api";
import type { BrandConfig } from "@/lib/shared/brand-config";
import { TECHQUITY_DEFAULTS } from "@/lib/shared/dynamic-brand-config";
import { create } from "zustand";

interface BrandStore {
  brandConfig: BrandConfig;
  isLoading: boolean;
  lastFetch: number | null;
  error: string | null;

  // Actions
  setBrandConfig: (config: BrandConfig) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  loadBrandConfig: () => Promise<void>;
  refreshBrandConfig: () => Promise<void>;
  clearCache: () => void;

  // Getters
  isStale: () => boolean;
  shouldRefresh: () => boolean;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const STORAGE_KEY = 'brand-config';

/**
 * Load brand configuration from Go backend API
 */
async function fetchBrandConfig(): Promise<BrandConfig> {
  try {
    const config = await brandingApi.getConfig();
    return toBrandConfig(config);
  } catch (error) {
    console.warn('Failed to fetch brand config from API, using defaults:', error);
    return TECHQUITY_DEFAULTS;
  }
}

/**
 * Load brand config from localStorage
 */
function loadFromStorage(): { config: BrandConfig | null; timestamp: number | null } {
  if (typeof window === 'undefined') return { config: null, timestamp: null };
  
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        config: parsed.config,
        timestamp: parsed.timestamp
      };
    }
  } catch (error) {
    console.warn('Failed to load brand config from storage:', error);
  }
  
  return { config: null, timestamp: null };
}

/**
 * Save brand config to localStorage
 */
function saveToStorage(config: BrandConfig, timestamp: number) {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      config,
      timestamp
    }));
  } catch (error) {
    console.warn('Failed to save brand config to storage:', error);
  }
}

export const useBrandStore = create<BrandStore>((set, get) => ({
  brandConfig: TECHQUITY_DEFAULTS,
  isLoading: false,
  lastFetch: 0, // Use 0 instead of null to indicate no fetch yet
  error: null,

  setBrandConfig: (config: BrandConfig) => {
    const now = Date.now();
    set({ 
      brandConfig: config, 
      lastFetch: now,
      error: null 
    });
    saveToStorage(config, now);
  },

  setLoading: (loading: boolean) => set({ isLoading: loading }),

  setError: (error: string | null) => set({ error }),

  loadBrandConfig: async () => {
    const { isLoading, shouldRefresh } = get();
    
    // Don't load if already loading
    if (isLoading) return;
    
    // Check if we have fresh cached data
    if (!shouldRefresh()) {
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const config = await fetchBrandConfig();
      get().setBrandConfig(config);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load brand config';
      console.error('❌ Brand Store: Failed to load brand config:', error);
      set({ error: errorMessage });
    } finally {
      set({ isLoading: false });
    }
  },

  refreshBrandConfig: async () => {
    // Clear both in-memory and localStorage cache to force refresh
    set({ lastFetch: 0 }); // Reset to 0 to force refresh
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
    }
    await get().loadBrandConfig();
  },

  clearCache: () => {
    set({ 
      brandConfig: TECHQUITY_DEFAULTS,
      lastFetch: 0,
      error: null 
    });
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
    }
  },

  isStale: () => {
    const { lastFetch } = get();
    if (!lastFetch) return true;
    return Date.now() - lastFetch > CACHE_DURATION;
  },

  shouldRefresh: () => {
    const { lastFetch } = get();
    
    // If lastFetch is 0, always refresh (forced refresh or initial load)
    if (lastFetch === 0) {
      return true;
    }
    
    // Check localStorage cache if no in-memory cache
    if (!lastFetch) {
      const { config, timestamp } = loadFromStorage();
      if (config && timestamp && (Date.now() - timestamp) < CACHE_DURATION) {
        // Use cached config
        set({ 
          brandConfig: config, 
          lastFetch: timestamp 
        });
        return false;
      }
      return true;
    }
    
    return get().isStale();
  },
}));

/**
 * Initialize brand store with cached data from localStorage
 */
export function initializeBrandStore() {
  if (typeof window === 'undefined') return;
  
  const { config, timestamp } = loadFromStorage();
  if (config && timestamp) {
    useBrandStore.setState({ 
      brandConfig: config, 
      lastFetch: timestamp 
    });
  }
}
